import { Component, OnInit, Input, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { EmployeeDTO, OrderModel, StatusDTO } from 'src/app/store/order/model';
import DataSource from 'devextreme/data/data_source';
import { AbstractOrderApiService } from 'src/app/core/services/abstract/abstract-order-api-service';
import { ServiceTypeFacade } from 'src/app/store/service-type/facade';
import { ServiceTypeModel } from 'src/app/store/service-type/model';
import { AbstractOrderUIService } from 'src/app/core/services/abstract/abstract-order-ui-service';
import { UserFacade } from 'src/app/store/user/facade';
import { OrderFacade } from 'src/app/store/order/facade';
import { AppConstants } from 'src/app/core/constants/app-constants';
import { AuthService } from '../../../../core/services/auth-service';
import { PermissionConstants } from '../../../../core/constants/permission-constants';
import { ServiceCatalogFacade } from '../../../../store/self-diagnostic/service-catalog/facade';
import { List } from '../../../../core/infrastructure/linq';
import { ServiceModel } from '../../../../store/self-diagnostic/service-catalog/model';

@Component({
  selector: 'app-order-base-attrs',
  templateUrl: './order-base-attrs.component.html',
  styleUrls: ['./order-base-attrs.component.scss'],
  standalone: false,
})
export class OrderBaseAttrsComponent implements OnInit {
  translate = inject(TranslateService);
  private serviceTypeFacade = inject(ServiceTypeFacade);
  private orderApiService = inject(AbstractOrderApiService);
  orderUIService = inject(AbstractOrderUIService);
  private userFacade = inject(UserFacade);
  private orderFacade = inject(OrderFacade);
  authService = inject(AuthService);
  private serviceCatalogFacade = inject(ServiceCatalogFacade);

  @Input() order: OrderModel;
  public permissions = PermissionConstants;

  serviceTypes$: Observable<ServiceTypeModel[]>;
  userId: number;

  dataSourceForWhom: any;
  valueForWhom: EmployeeDTO;

  dataSourceInitiator: any;
  valueInitiator: EmployeeDTO;

  public isUkLanguage: boolean;
  private loadedForWhom: boolean = false;
  private loadedInitiator: boolean = false;
  public service: ServiceModel;

  ngOnInit(): void {
    this.isUkLanguage = this.translate.currentLang == AppConstants.LANGUAGE_UK;

    this.serviceTypes$ = this.serviceTypeFacade.allServiceTypes$;

    this.dataSourceForWhom = new Array<EmployeeDTO>();
    if (this.order && this.order.employeeFor) {
      this.dataSourceForWhom.push(this.order.employeeFor);
      this.valueForWhom = this.order.employeeFor;
    }

    this.dataSourceInitiator = new Array<EmployeeDTO>();
    if (this.order && this.order.employee) {
      this.dataSourceInitiator.push(this.order.employee);
      this.valueInitiator = this.order.employee;
    }

    this.userFacade.selected().subscribe(x => {
      if (x) {
        this.userId = x.id;
      }
    });

    this.serviceCatalogFacade.entities$.subscribe(s$ => {
      let services = new List<ServiceModel>(s$);

      if (services.Any()) {
        this.service = services.FirstOrDefault(s => s.code == this.order.task.id && s.category.code == this.order.service.id);
      }
    });
  }

  onOpenedForWhom() {
    if (!this.loadedForWhom) {
      this.loadedForWhom = true;
      this.dataSourceForWhom = new DataSource({
        key: 'ID',
        load: async loadOptions => {
          if ((loadOptions.searchValue && (<string>loadOptions.searchValue).length > 3) || this.valueForWhom) {
            return await this.orderApiService
              .getForWhomEmployees(
                this.userId,
                this.order.service.id,
                this.order.task.id,
                loadOptions.searchValue ? loadOptions.searchValue : this.valueForWhom ? this.valueForWhom.fio : ''
              )
              .toPromise()
              .then(response => {
                return {
                  data: response.result,
                };
              });
          }
          return [];
        },
      });
    }
  }

  onValueChangedForWhom() {
    if (this.order.coordinator) {
      this.order.coordinator.id = null;
    }

    if (this.loadedForWhom) {
      this.order.employeeFor = this.valueForWhom;
      this.orderFacade.setSelectedEmployeeForId(this.order.employeeFor?.id);
    }
  }

  onOpenedInitiator() {
    if (!this.loadedInitiator) {
      this.loadedInitiator = true;
      this.dataSourceInitiator = new DataSource({
        key: 'ID',
        load: async loadOptions => {
          if ((loadOptions.searchValue && (<string>loadOptions.searchValue).length > 3) || this.valueInitiator) {
            return await this.orderApiService
              .getInitiatorEmployees(
                this.userId,
                this.order.service.id,
                this.order.task.id,
                loadOptions.searchValue ? loadOptions.searchValue : this.valueInitiator ? this.valueInitiator.fio : ''
              )
              .toPromise()
              .then(response => {
                return {
                  data: response.result,
                };
              });
          }
          return [];
        },
      });
    }
  }

  onValueChangedInitiator() {
    if (this.loadedInitiator) {
      this.order.employee = this.valueInitiator;
    }
  }

  getCategoryFieldName() {
    return this.translate.currentLang == AppConstants.LANGUAGE_UK ? 'nameUa' : 'nameRu';
  }

  getServiceCategoryName(): string {
    let serviceCategoryName: string;

    if (this.order && this.order.id) {
      serviceCategoryName = this.order.service?.value;
    }

    if (this.service) {
      if (this.isUkLanguage) {
        serviceCategoryName = this.service.category?.webNameUa ? this.service.category?.webNameUa : this.service.category?.nameUa;
      } else {
        serviceCategoryName = this.service.category?.webNameRu ? this.service.category?.webNameRu : this.service.category?.nameRu;
      }
    }

    return serviceCategoryName;
  }

  getServiceName(): string {
    let serviceName: string;

    if (this.order && this.order.id) {
      serviceName = this.order.task?.value;
    }

    if (this.service) {
      if (this.isUkLanguage) {
        serviceName = this.service.nameUa;
      } else {
        serviceName = this.service.nameRu;
      }
    }

    return serviceName;
  }
}
